import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../shared/Card';
import { getReservations, mapApiToGridReservations } from '../../services/reservationService';
import { getTodayString, addDays } from '../../utils/dateUtils';
import './LandingDashboard.css';

interface TodayCounts {
  arrivals: number;
  departures: number;
}

const LandingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [todayCounts, setTodayCounts] = useState<TodayCounts | null>(null);

  useEffect(() => {
    const today = getTodayString();
    Promise.all([
      getReservations({ checkInFrom: today, checkInTo: today, activeOnly: true }),
      getReservations({ checkInFrom: addDays(today, -30), checkInTo: today, activeOnly: true }),
    ]).then(([arrivalsApi, departuresApi]) => {
      const arrivals = mapApiToGridReservations(arrivalsApi)
        .filter(r => r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out');
      const uniqueArrivals = Array.from(new Map(arrivals.map(r => [`${r.id}-${r.siteId}`, r])).values());

      const departures = mapApiToGridReservations(departuresApi)
        .filter(r => r.checkOut === today && (r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out'));
      const uniqueDepartures = Array.from(new Map(departures.map(r => [`${r.id}-${r.siteId}`, r])).values());

      setTodayCounts({ arrivals: uniqueArrivals.length, departures: uniqueDepartures.length });
    }).catch(() => {});
  }, []);

  return (
    <div className="landing-dashboard">
      <h1 className="landing-title">Manager Dashboard</h1>
      <p className="landing-subtitle">Welcome to the campground management system</p>

      <div className="landing-cards">
        <Card
          title="Reservations"
          description="View and manage all campsite reservations. See the schedule grid, check booking details, and handle modifications."
          icon="📅"
          onClick={() => navigate('/reservations')}
        />

        <Card
          title="Members"
          description="Access member profiles, view their reservation history, and manage member information."
          icon="👥"
          onClick={() => navigate('/members')}
        />

        <Card
          title="Sites"
          description="View all campsites, check availability status, and monitor site conditions across the campground."
          icon="🏕️"
          onClick={() => navigate('/sites')}
        />

        <Card
          title="Campground System Configuration"
          description="Configure site rates, fees, taxes, and other system settings."
          icon="⚙️"
          onClick={() => navigate('/config')}
        />

        <Card
          title="Analysis"
          description="Business analytics: sales trends, occupancy, payments, and anomaly detection."
          icon="📊"
          onClick={() => navigate('/analysis')}
        />

        <Card
          title="Today's Report"
          icon="📋"
          onClick={() => navigate('/daily-report')}
        >
          {todayCounts !== null ? (
            <div className="landing-today-counts">
              <span className="landing-today-count landing-today-count--arrivals">
                {todayCounts.arrivals} arriving
              </span>
              <span className="landing-today-divider">·</span>
              <span className="landing-today-count landing-today-count--departures">
                {todayCounts.departures} departing
              </span>
            </div>
          ) : (
            <p className="landing-card-description">View today's check-ins and check-outs.</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LandingDashboard;
